import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Alert,
    AlertIcon,
    Box,
    CloseButton,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    InputGroup,
    InputRightAddon,
    List,
    ListItem,
    Text,
    useColorModeValue,
    useOutsideClick,
    VStack
} from "@chakra-ui/react";

const GoogleMapsAddressInput = ({currentStore, setCurrentStore}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isLoadingScript, setIsLoadingScript] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);

    const predictionsRef = useRef(null);
    const inputRef = useRef(null);

    // Initialize Google Maps
    useEffect(() => {
        const initializeGoogleMaps = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                setIsLoaded(true);
                setIsLoadingScript(false);
                return;
            }

            // Check if script is already loading
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                existingScript.addEventListener('load', () => {
                    setIsLoaded(true);
                    setIsLoadingScript(false);
                });
                return;
            }

            // Load Google Maps script
            setIsLoadingScript(true);
            const script = document.createElement('script');
            let key = 'AIzaSyAxxkGHlnAKnGQz_KYJdg8Ztd8PhrF6S7U';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                setIsLoaded(true);
                setIsLoadingScript(false);
            };
            script.onerror = () => {
                console.error('Failed to load Google Maps API');
                setIsLoadingScript(false);
            };
            document.head.appendChild(script);
        };

        initializeGoogleMaps();
    }, []);

    // Fallback to manual search if new API is not available
    const handleAddressSearchFallback = useCallback((query) => {
        if (!query || !isLoaded) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }

        // Use the old AutocompleteService API
        if (window.google?.maps?.places?.AutocompleteService) {
            setIsLoadingPredictions(true);

            const service = new window.google.maps.places.AutocompleteService();
            const request = {
                input: query,
                types: ['address'],
                componentRestrictions: {country: 'us'}
            };

            service.getPlacePredictions(request, (predictions, status) => {
                setIsLoadingPredictions(false);

                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {

                    setPredictions(predictions);
                    setShowPredictions(true);
                } else {

                    setPredictions([]);
                    setShowPredictions(false);
                }
            });
        } else {
            setIsLoadingPredictions(false);

        }
    }, [isLoaded]);

    // Handle address search with new AutocompleteSuggestion API
    const handleAddressSearch = useCallback(async (query) => {
        if (!query || !isLoaded) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }

        setIsLoadingPredictions(true);

        try {
            // Try new API first
            if (window.google?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
                const request = {
                    input: query,
                    includedPrimaryTypes: ['address'],
                    locationRestriction: {
                        country: 'us'
                    }
                };

                const {suggestions} = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                if (suggestions && suggestions.length > 0) {

                    setPredictions(suggestions);
                    setShowPredictions(true);
                } else {
                    setPredictions([]);
                    setShowPredictions(false);
                }
            } else {
                // Fallback to old API
                handleAddressSearchFallback(query);
            }
        } catch (error) {
            console.error('Error fetching suggestions with new API:', error);
            // Fallback to old API on error
            handleAddressSearchFallback(query);
        } finally {
            setIsLoadingPredictions(false);
        }
    }, [isLoaded, handleAddressSearchFallback]);

    // Handle place selection with new Place API
    const handlePlaceSelect = useCallback(async (suggestion) => {


        try {
            // Create a Place instance from the suggestion
            const place = suggestion.toPlace();

            // Fetch place details with the new API
            await place.fetchFields(['addressComponents', 'formattedAddress']);


            // Parse address components using the new format
            const addressComponents = {};
            if (place.addressComponents) {
                place.addressComponents.forEach(component => {
                    const types = component.types;


                    if (types.includes('street_number')) {
                        addressComponents.streetNumber = component.longText;
                    }
                    if (types.includes('route')) {
                        addressComponents.streetName = component.longText;
                    }
                    if (types.includes('subpremise')) {
                        addressComponents.apartment = component.longText;
                    }
                    if (types.includes('neighborhood')) {
                        addressComponents.city = component.longText;
                    }
                    if (types.includes('administrative_area_level_1')) {
                        addressComponents.state = component.shortText;
                    }
                    if (types.includes('postal_code')) {
                        addressComponents.zipCode = component.longText;
                    }
                });
            }
            let latitude = 0;
            let longitude = 0
            if (place.geometry && place.geometry.location) {
                latitude = place.geometry.location.lat();
                longitude = place.geometry.location.lng();
            }

            // Update store with parsed address
            const updatedStore = {
                ...currentStore,
                address: place.formattedAddress || '',
                streetNumber: addressComponents.streetNumber || '',
                streetName: addressComponents.streetName || '',
                apartment: addressComponents.apartment || currentStore.apartment || '',
                city: addressComponents.city || '',
                state: addressComponents.state || 'NY',
                zipCode: addressComponents.zipCode || '',
                latitude: latitude,
                longitude: longitude,
            };


            setCurrentStore(updatedStore);
            setSearchValue(place.formattedAddress || '');
            setShowPredictions(false);
            setPredictions([]);

        } catch (error) {
            console.error('Error fetching place details with new API:', error);
        }
    }, [currentStore, setCurrentStore]);

    // Handle place selection fallback
    const handlePlaceSelectFallback = useCallback((placeId) => {


        if (!window.google?.maps?.places?.PlacesService) {
            console.error('PlacesService not available');
            return;
        }

        const service = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );

        const request = {
            placeId: placeId,
            fields: ['address_components', 'formatted_address', 'geometry']
        };

        service.getDetails(request, (place, status) => {


            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                debugger
                // Parse address components (legacy format)
                const addressComponents = {};
                if (place.address_components) {
                    place.address_components.forEach(component => {
                        const types = component.types;


                        if (types.includes('street_number')) {
                            addressComponents.streetNumber = component.long_name;
                        }
                        if (types.includes('route')) {
                            addressComponents.streetName = component.long_name;
                        }
                        if (types.includes('subpremise')) {
                            addressComponents.apartment = component.long_name;
                        }
                        if (types.includes('neighborhood')) {
                            addressComponents.city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            addressComponents.state = component.short_name;
                        }
                        if (types.includes('postal_code')) {
                            addressComponents.zipCode = component.long_name;
                        }
                    });
                }

                let latitude = 0;
                let longitude = 0;
                if (place.geometry.location) {
                    latitude = place.geometry.location.lat();
                    longitude = place.geometry.location.lng();
                }
                const updatedStore = {
                    ...currentStore,
                    address: place.formatted_address || '',
                    streetNumber: addressComponents.streetNumber || '',
                    streetName: addressComponents.streetName || '',
                    apartment: addressComponents.apartment || currentStore.apartment || '',
                    city: addressComponents.city || '',
                    state: addressComponents.state || 'NY',
                    zipCode: addressComponents.zipCode || '',
                    latitude: latitude,
                    longitude: longitude
                };


                setCurrentStore(updatedStore);
                setSearchValue(place.formatted_address || '');
                setShowPredictions(false);
                setPredictions([]);
            } else {
                console.error('Failed to get place details:', status);
            }
        });
    }, [currentStore, setCurrentStore]);

    // Handle input changes with debouncing
    useEffect(() => {
        if (!searchValue) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            handleAddressSearch(searchValue);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchValue, handleAddressSearch]);

    // Handle input changes
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        setCurrentStore(prev => ({
            ...prev,
            address: value
        }));
    };

    // Handle clear address
    const handleClearAddress = () => {
        setSearchValue("");
        setShowPredictions(false);
        setPredictions([]);
        setCurrentStore(prev => ({
            ...prev,
            address: '',
            streetNumber: '',
            streetName: '',
            city: '',
            state: 'NY',
            zipCode: '',
            latitude: 0,
            longitude: 0,
        }));
    };

    // Handle manual input changes for individual fields
    const handleFieldChange = (field, value) => {
        setCurrentStore(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle prediction click - Fixed logic
    const handlePredictionClick = (prediction) => {


        // Check if this is a new API suggestion
        if (prediction.placePrediction && prediction.toPlace && typeof prediction.toPlace === 'function') {

            handlePlaceSelect(prediction);
        }
        // Check if this is an old API prediction
        else if (prediction.place_id) {

            handlePlaceSelectFallback(prediction.place_id);
        }
        // Direct suggestion object (new API format)
        else if (prediction.toPlace && typeof prediction.toPlace === 'function') {

            handlePlaceSelect(prediction);
        } else {
            console.error('Unknown prediction format:', prediction);
        }
    };


    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const phoneNumber = value.replace(/\D/g, '');

        // Don't format if empty
        if (!phoneNumber) return '';

        // Format based on length
        if (phoneNumber.length <= 3) {
            return `(${phoneNumber}`;
        } else if (phoneNumber.length <= 6) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    const handlePhoneNumberChange = (e) => {
        const input = e.target.value;
        const formatted = formatPhoneNumber(input);
        handleFieldChange('phoneNumber', formatted);
    };
    // Close predictions when clicking outside
    useOutsideClick({
        ref: predictionsRef,
        handler: () => setShowPredictions(false),
    });

    const borderColor = useColorModeValue("gray.200", "gray.600");
    const bgColor = useColorModeValue("white", "gray.800");

    // Sync search value with currentStore.address on component mount/update
    useEffect(() => {
        if (currentStore.address && currentStore.address !== searchValue) {
            setSearchValue(currentStore.address);
        }
    }, [currentStore.address, searchValue]);

    if (isLoadingScript) {
        return (
            <Alert status="info" mb={4}>
                <AlertIcon/>
                <Box>
                    <Text fontWeight="bold">Loading Google Maps...</Text>
                    <Text fontSize="sm">Setting up address autocomplete...</Text>
                </Box>
            </Alert>
        );
    }

    return (
        <VStack spacing={4} align="stretch">
            {/* Google Places Autocomplete */}
            <FormControl position="relative">
                <FormLabel>Search Address</FormLabel>
                <InputGroup>
                    <Input
                        ref={inputRef}
                        placeholder="Start typing an address..."
                        value={searchValue}
                        onChange={handleInputChange}
                        onFocus={() => {
                            if (predictions.length > 0) {
                                setShowPredictions(true);
                            }
                        }}
                        bg="white"
                        _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px blue.500"
                        }}
                    />
                    {searchValue && (
                        <InputRightAddon>
                            <CloseButton onClick={handleClearAddress}/>
                        </InputRightAddon>
                    )}
                </InputGroup>

                {/* Predictions Dropdown */}
                {showPredictions && (predictions.length > 0 || isLoadingPredictions) && (
                    <Box
                        ref={predictionsRef}
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        zIndex={1000}
                        bg={bgColor}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="md"
                        boxShadow="lg"
                        maxHeight="200px"
                        overflowY="auto"
                        mt={1}
                    >
                        {isLoadingPredictions ? (
                            <Box p={3} textAlign="center">
                                <Text fontSize="sm" color="gray.500">Loading suggestions...</Text>
                            </Box>
                        ) : (
                            <List spacing={0}>
                                {predictions.map((prediction, index) => {
                                    // Handle both new and old API formats
                                    const mainText = prediction.placePrediction?.text?.text ||
                                        prediction.structured_formatting?.main_text ||
                                        prediction.description;
                                    const secondaryText = prediction.placePrediction?.structuredFormat?.secondaryText?.text ||
                                        prediction.structured_formatting?.secondary_text ||
                                        '';

                                    return (
                                        <ListItem
                                            key={prediction.placePrediction?.placeId || prediction.place_id || index}
                                            p={3}
                                            cursor="pointer"
                                            _hover={{bg: "gray.100"}}
                                            onClick={() => handlePredictionClick(prediction)}
                                            borderBottom="1px solid"
                                            borderBottomColor="gray.100"
                                            _last={{borderBottom: "none"}}
                                        >
                                            <Text fontSize="sm" fontWeight="medium">
                                                {mainText}
                                            </Text>
                                            {secondaryText && (
                                                <Text fontSize="xs" color="gray.500">
                                                    {secondaryText}
                                                </Text>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                )}

                <Text fontSize="xs" color="gray.500" mt={1}>
                    {isLoaded ?
                        "Select an address from the dropdown to auto-fill details below" :
                        "Google Maps autocomplete not available. Enter address details manually below."
                    }
                </Text>
            </FormControl>

            {/* Manual Address Input Fields */}
            <Box p={4} border="1px solid" borderColor={borderColor} borderRadius="md">
                <Heading fontSize="md" mb={3}>Address Details</Heading>

                {/* Street Address Row */}
                <HStack spacing={4} mb={4}>
                    <FormControl flex="1">
                        <FormLabel fontSize="sm">Street Number</FormLabel>
                        <Input
                            placeholder="123"
                            value={currentStore.streetNumber || ''}
                            onChange={(e) => handleFieldChange('streetNumber', e.target.value)}
                        />
                    </FormControl>
                    <FormControl flex="3">
                        <FormLabel fontSize="sm">Street Name</FormLabel>
                        <Input
                            placeholder="Main Street"
                            value={currentStore.streetName || ''}
                            onChange={(e) => handleFieldChange('streetName', e.target.value)}
                        />
                    </FormControl>
                    <FormControl flex="1">
                        <FormLabel fontSize="sm">Apt/Unit</FormLabel>
                        <Input
                            placeholder="3B"
                            value={currentStore.apartment || ''}
                            onChange={(e) => handleFieldChange('apartment', e.target.value)}
                        />
                    </FormControl>
                </HStack>

                {/* City, State, ZIP Row */}
                <HStack spacing={4}>
                    <FormControl flex="2">
                        <FormLabel fontSize="sm">City</FormLabel>
                        <Input
                            placeholder="New York"
                            value={currentStore.city || ''}
                            onChange={(e) => handleFieldChange('city', e.target.value)}
                        />
                    </FormControl>
                    <FormControl flex="1">
                        <FormLabel fontSize="sm">State</FormLabel>
                        <Input
                            placeholder="NY"
                            value={currentStore.state || ''}
                            onChange={(e) => handleFieldChange('state', e.target.value)}
                        />
                    </FormControl>
                    <FormControl flex="1">
                        <FormLabel fontSize="sm">ZIP Code</FormLabel>
                        <Input
                            placeholder="10001"
                            value={currentStore.zipCode || ''}
                            onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                        />
                    </FormControl>
                </HStack>

                {/* Full Address (Editable) */}
                <FormControl mt={4}>
                    <FormLabel fontSize="sm">Full Address</FormLabel>
                    <Input
                        value={currentStore.address || ''}
                        onChange={(e) => handleFieldChange('address', e.target.value)}
                        placeholder="Complete address will appear here"
                        bg="white"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                        This field is automatically filled by the address search above, but you can edit it manually if
                        needed.
                    </Text>
                </FormControl>
            </Box>

            {/* Phone Number Field */}
            <FormControl>
                <FormLabel>Phone Number</FormLabel>
                <Input
                    placeholder="(555) 123-4567"
                    value={currentStore.phoneNumber || ''}
                    onChange={handlePhoneNumberChange}
                    maxLength={14} // (555) 123-4567 = 14 characters
                />
            </FormControl>

            {/* Status Info */}
            {!isLoaded && !isLoadingScript && (
                <Alert status="warning" size="sm">
                    <AlertIcon/>
                    <Text fontSize="sm">
                        Google Maps autocomplete is not available. You can still enter all address details manually
                        above.
                    </Text>
                </Alert>
            )}
        </VStack>
    );
};

export default GoogleMapsAddressInput;
